/**
 * JotRx Encounter CTA
 *
 * CTA for the encounter/recording screens. Two visual variants:
 *
 * - "inline": For the PatientContextBar (V2 encounters).
 *   When setup is incomplete: shows centered "E-Prescribe Now Available | Start Setup →" pill.
 *   When fully_setup: shows right-aligned 3-state prescribe button:
 *     1. No patient selected → disabled outlined "Select Patient to E-Prescribe"
 *     2. Patient selected, not connected → outlined "Set Up Patient for E-Rx"
 *     3. Patient selected + connected → filled "E-Prescribe this Patient"
 *
 * - "banner": For the TopBar area (V1 encounters).
 *   When setup is incomplete: shows centered full-width setup prompt bar.
 *   When fully_setup: shows centered 3-state prescribe button (same 3 states).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MedicationIcon from '@mui/icons-material/Medication';
import { alpha, Box, Button, IconButton, keyframes, Typography } from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { useStatsigClient } from '@statsig/react-bindings';

import { useJotRxEncounterAction } from '../hooks/useJotRxEncounterAction';

import { EPrescriptionPatientConnectModal } from './EPrescriptionPatientConnectModal';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const DISMISS_STORAGE_KEY = 'jotrx_encounter_cta_dismissed';

interface JotRxEncounterCtaProps {
  patientId?: string | null;
  variant?: 'inline' | 'banner';
}

// --- Component ---

export function JotRxEncounterCta({ patientId, variant = 'inline' }: JotRxEncounterCtaProps) {
  const {
    setupState,
    isVisible,
    isFullySetup,
    isJotRxPurchasable,
    isPatientConnected,
    isPatientStatusLoading,
    handleClick,
    showConnectModal,
    setShowConnectModal,
    handleConnectSuccess,
    connectModalPatientData,
  } = useJotRxEncounterAction({ patientId });
  const { jotrx } = useEventTracking();
  const { client } = useStatsigClient();
  const isAdGateEnabled = useFeatureGate('jotrx_ad');

  const { setupCallUrl, setupCtaUrl } = useMemo(() => {
    const config = client.getDynamicConfig('jotrx_setup_config');
    const value = config.value as { setup_call_url?: string; setup_cta_url?: string };
    return {
      setupCallUrl: value.setup_call_url || '',
      setupCtaUrl: value.setup_cta_url || '',
    };
  }, [client]);

  const handleSetupCtaClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!setupCtaUrl) return;
      jotrx.demoRequested({ source: 'encounter_cta', setup_state: setupState });
      window.open(setupCtaUrl, '_blank', 'noopener,noreferrer');
    },
    [jotrx, setupCtaUrl, setupState],
  );

  const handleBookCall = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!setupCallUrl) return;
      jotrx.setupCallBooked({ source: 'encounter_cta' });
      window.open(setupCallUrl, '_blank', 'noopener,noreferrer');
    },
    [jotrx, setupCallUrl],
  );

  const [dismissedForState, setDismissedForState] = useState(
    () => localStorage.getItem(DISMISS_STORAGE_KEY) || '',
  );

  const isDismissed = !isFullySetup && dismissedForState === setupState;

  // Track banner impressions (matches JotRxSetupBanner behavior)
  const isBannerVisible = isAdGateEnabled && !isFullySetup && !isDismissed;
  useEffect(() => {
    if (isBannerVisible) {
      jotrx.bannerShown({ setup_state: setupState });
    }
  }, [isBannerVisible, jotrx, setupState]);

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setDismissedForState(setupState);
      localStorage.setItem(DISMISS_STORAGE_KEY, setupState);
      jotrx.bannerDismissed({ setup_state: setupState, source: 'encounter_cta' });
    },
    [setupState, jotrx],
  );

  // --- Fully setup → 3-state prescribe button (gated by entitlement/bundle) ---
  if (isVisible && isFullySetup) {
    const hasPatient = !!patientId;
    // While patient status is loading, treat as "no patient" to avoid flashing the needsConnect state
    const resolved = hasPatient && !isPatientStatusLoading;
    const needsConnect = resolved && !isPatientConnected;
    const readyToPrescribe = resolved && isPatientConnected;

    const button = (
      <>
        <Button
          variant={readyToPrescribe ? 'contained' : 'outlined'}
          size="small"
          disabled={!hasPatient}
          startIcon={<EditNoteIcon sx={{ fontSize: '16px !important' }} />}
          onClick={handleClick}
          sx={{
            borderRadius: 0,
            textTransform: 'none',
            py: 0.75,
            px: 2,
            fontWeight: 600,
            fontSize: '0.8125rem',
            whiteSpace: 'nowrap',
            boxShadow: 0,
            ...(readyToPrescribe && {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            }),
            ...(!readyToPrescribe && hasPatient && {
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
              },
            }),
            ...(!hasPatient && {
              borderColor: 'primary.main',
              color: 'primary.main',
              opacity: 0.5,
              '&.Mui-disabled': {
                borderColor: 'primary.main',
                color: 'primary.main',
                opacity: 0.5,
              },
            }),
          }}
        >
          {readyToPrescribe
            ? 'Prescribe'
            : needsConnect
              ? 'Set Up Patient for E-Rx'
              : 'Select Patient to E-Prescribe'}
        </Button>

        {patientId && (
          <EPrescriptionPatientConnectModal
            open={showConnectModal}
            onClose={() => setShowConnectModal(false)}
            patientId={patientId}
            patientData={connectModalPatientData}
            onSuccess={handleConnectSuccess}
          />
        )}
      </>
    );

    // V2 (inline): right-align in the PatientContextBar
    if (variant === 'inline') {
      return <Box sx={{ ml: 'auto' }}>{button}</Box>;
    }
    // V1 (banner): center-align in the top bar
    return <Box sx={{ mx: 'auto' }}>{button}</Box>;
  }

  // --- Ad banner: gated by jotrx_ad feature flag ---
  if (!isAdGateEnabled || isFullySetup) return null;
  if (isDismissed) return null;

  // --- Banner variant: full-width bar for V1 encounter TopBar area ---
  if (variant === 'banner') {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          mx: 'auto',
          width: '100%',
          maxWidth: 1400,
          px: 2,
          py: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: theme => alpha(theme.palette.primary.main, 0.15),
          bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
        }}
      >
        {/* Left: icon + label */}
        <EditNoteIcon sx={{ fontSize: 16, color: 'primary.main', mr: 1 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'primary.main', whiteSpace: 'nowrap' }}
        >
          E-Prescribe Now Available
        </Typography>

        {/* Right: start setup + book a call + dismiss */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {setupCtaUrl && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleSetupCtaClick}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.5,
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: 'background.paper',
                whiteSpace: 'nowrap',
                animation: `${pulse} 2s ease-in-out infinite`,
                '&:hover': {
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                  borderColor: 'primary.dark',
                },
              }}
            >
              Get a demo
            </Button>
          )}
          {setupCallUrl && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon sx={{ fontSize: '14px !important' }} />}
              onClick={handleBookCall}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.5,
                borderColor: 'divider',
                color: 'text.primary',
                bgcolor: 'background.paper',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'divider' },
              }}
            >
              Book a setup call
            </Button>
          )}
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss"
            sx={{
              p: 0.5,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // --- Inline variant: compact bar for PatientContextBar (V2) ---
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexShrink: 0,
        ml: 'auto',
        px: 2,
        py: 0.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme => alpha(theme.palette.info.main, 0.2),
        bgcolor: theme => alpha(theme.palette.info.main, 0.04),
      }}
    >
      {/* Left: icon + label (clickable → setup link) */}
      <Box
        onClick={setupCtaUrl ? handleSetupCtaClick : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: setupCtaUrl ? 'pointer' : 'default',
          '&:hover': setupCtaUrl ? { opacity: 0.8 } : {},
        }}
      >
        <MedicationIcon sx={{ fontSize: 18, color: 'info.main', flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'info.main', whiteSpace: 'nowrap' }}
        >
          E-Prescribe Now Available
        </Typography>
      </Box>

      {/* Right: setup + book a call + dismiss */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {setupCtaUrl && (
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleSetupCtaClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
              whiteSpace: 'nowrap',
              boxShadow: 0,
              bgcolor: 'info.main',
              animation: `${pulse} 2s ease-in-out infinite`,
              '&:hover': { bgcolor: 'info.dark' },
            }}
          >
            Get a demo
          </Button>
        )}
        {setupCallUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleBookCall}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
              borderColor: 'divider',
              color: 'text.primary',
              bgcolor: 'background.paper',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'divider' },
            }}
          >
            Book a setup call
          </Button>
        )}
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss"
          sx={{
            p: 0.5,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
